// The lens-facing API the prototypes were written against: goal switches, working
// memory, links between lenses and toasts. One shell serves a project view: the open lens
// and the top bar (memory panel, search, activity strip) share it, so a change in one shows
// in the other. For someone signed in, memory, library and goal switches are saved to their
// account in the workspace and follow them between devices (DEC-032, DEC-041); the shell keeps
// a working copy and the account's answer to each change wins. A visitor who isn't signed in
// (the public demo) keeps them in this browser, namespaced by tenant and project (DEC-023).
import { api } from "./api.js";
import { legacyDay } from "./day.js";

const DAY_MS = 864e5;
const OFF_PENALTY = 0.18; // what a switched-off best goal takes off a paper's score, times the goal's weight
const CAP = 7;
const HOLD_DAYS = 7;
const SNAP_FIELDS = ["id", "area", "kind", "title", "minutes", "cats", "summary", "authors", "url", "pdf", "arxiv", "signals", "goal"];

const localKey = (tenant, project) => `pot:${tenant.slug}:${project.slug}:shell`;

/** The goal that counts for a paper when the goals in `off` are switched off: { id, fit }, id null when none fits. */
export function bestOn(fits, off) {
  let best = { id: null, fit: 0 };
  for (const [id, fit] of Object.entries(fits || {})) if (!off.includes(id) && fit > best.fit) best = { id, fit };
  return best;
}
/** A paper's salience from the rest of its score and a goal fit, rounded as the day rounds it (daybuilder.js salienceOf). */
export const scoreWith = (rest, steer, fit) => Number(Math.min(0.99, Math.max(0.05, rest + steer * fit)).toFixed(2));

/** Asks the project view to open the explainer's drawer for a paper: { id, title, authors?, url?, … }. */
export const openExplainer = p => window.dispatchEvent(new CustomEvent("pot:explain", { detail: p }));

function short(t) {
  return t.length > 52 ? t.slice(0, 50).replace(/\s+\S*$/, "") + "…" : t;
}

/**
 * The signed-in person's saved state for a project. Whatever this browser kept before they
 * signed in here moves into the account once, and leaves the browser.
 */
export async function loadSaved(tenant, project) {
  const key = localKey(tenant, project);
  let local = null;
  try {
    local = JSON.parse(localStorage.getItem(key));
  } catch {
    local = null;
  }
  if (!local || !(local.held?.length || local.library?.length || local.off?.length)) return api.mine(project.slug);
  const saved = await api.importMine(project.slug, { held: local.held || [], library: local.library || [], off: local.off || [] });
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage blocked: nothing to remove */
  }
  return saved;
}

/** saved: the signed-in person's state from loadSaved, or null for a visitor who isn't signed in. */
export function createShell({ tenant, project, payload, navigate, saved = null }) {
  const key = localKey(tenant, project);
  const account = Boolean(saved);
  let lens = "today"; // the open lens, set by LensHost; held items remember where they were held
  let st = saved ? { held: saved.held, library: saved.library, off: saved.off, team: saved.team || [] } : null;
  if (!account) {
    try {
      st = JSON.parse(localStorage.getItem(key));
    } catch {
      st = null;
    }
  }
  if (!st || !Array.isArray(st.held) || !Array.isArray(st.off) || !Array.isArray(st.library)) st = { off: [], held: [], library: [] };
  if (!account) st.team = []; // the team shelf is for people signed in (DEC-042)
  const save = () => {
    if (account) return;
    try {
      localStorage.setItem(key, JSON.stringify(st));
    } catch {
      /* storage blocked: state lasts for this page only */
    }
  };

  const D = legacyDay(payload);
  const paper = id => D.paperById[id];
  const listeners = new Map();
  const emit = (type, detail) =>
    (listeners.get(type) || new Set()).forEach(fn => {
      try {
        fn(detail);
      } catch (err) {
        console.error(err);
      }
    });

  // Held items keep a copy of the paper, so they outlive the day that produced them.
  const snap = p => {
    const c = {};
    for (const f of SNAP_FIELDS) if (p[f] != null) c[f] = p[f];
    c.signals = c.signals || [];
    c.salience = p.baseSalience ?? p.salience;
    c.day = D.date;
    return c;
  };
  const adopt = h => {
    if (D.paperById[h.id]) {
      if (!h.p) h.p = snap(D.paperById[h.id]);
      return true;
    }
    if (!h.p || !D.areaById[h.p.area]) return false;
    D.paperById[h.id] = { ...h.p, earlier: true };
    return true;
  };
  st.held = st.held.filter(h => Date.now() - h.at < HOLD_DAYS * DAY_MS && adopt(h));
  st.library = st.library.filter(adopt);
  st.team = st.team.filter(adopt);
  st.off = st.off.filter(id => D.goalById[id]);
  save();

  // Goal switches re-rank the stage in place (DEC-008). A paper whose best goal is switched off is
  // scored again with the best goal this reader still has on (DEC-047). A day built before the day
  // carried each goal's fit falls back to taking OFF_PENALTY times the goal's weight off.
  const applyGoals = () => {
    for (const p of D.papers) {
      if (p.baseSalience == null) p.baseSalience = p.salience;
      let s = p.baseSalience;
      if (D.goalSteer != null) {
        const on = bestOn(p.fits, st.off);
        if (on.fit < bestOn(p.fits, []).fit) s = scoreWith(p.rest, D.goalSteer, on.fit);
      } else if (p.goal && st.off.includes(p.goal) && D.goalById[p.goal]) s -= OFF_PENALTY * D.goalById[p.goal].weight;
      p.salience = Math.max(0, Math.round(s * 1000) / 1000);
    }
    D.ranked = D.papers.slice().sort((a, b) => b.salience - a.salience);
    for (const g of D.goals) g.on = !st.off.includes(g.id);
  };
  applyGoals();

  const fade = x => {
    const h = typeof x === "string" ? st.held.find(i => i.id === x) : x;
    return h ? Math.max(0, Math.min(1, 1 - (Date.now() - h.at) / (HOLD_DAYS * DAY_MS))) : 0;
  };
  const addToLibrary = id => {
    st.library = st.library.filter(h => h.id !== id);
    st.library.unshift({ id, at: Date.now(), p: snap(paper(id)) });
  };
  const toast = msg => window.dispatchEvent(new CustomEvent("pot:toast", { detail: msg }));
  const areaCache = new Map(); // area id -> a promise of that area's other items, or null

  // Signed in: changes go to the account one at a time, and the answer to the last one is what
  // stays, since another device may have changed things meanwhile. Coming back to the page
  // picks up what changed elsewhere.
  const sig = list => list.map(h => `${h.id}@${h.at}`).join(",");
  const apply = s => {
    const held = s.held.filter(h => Date.now() - h.at < HOLD_DAYS * DAY_MS && adopt(h));
    const library = s.library.filter(adopt);
    const team = (s.team || []).filter(adopt);
    const off = s.off.filter(id => D.goalById[id]);
    const heldChanged = sig(held) !== sig(st.held) || sig(library) !== sig(st.library) || sig(team) !== sig(st.team);
    const goalsChanged = off.slice().sort().join() !== st.off.slice().sort().join();
    st = { held, library, off, team };
    if (goalsChanged) {
      applyGoals();
      emit("goals", { synced: true });
    }
    if (heldChanged) emit("held", { synced: true });
  };
  let queue = Promise.resolve();
  let inflight = 0;
  const refresh = () => {
    if (account && !inflight) api.mine(project.slug).then(s => !inflight && apply(s), () => {});
  };
  const sync = send => {
    if (!account) return;
    inflight++;
    queue = queue.then(send).then(
      s => --inflight === 0 && apply(s),
      () => {
        inflight--;
        toast("That didn't reach your account, so your other devices won't see it yet. Check your connection.");
        refresh();
      }
    );
  };
  const onVisible = () => document.visibilityState === "visible" && refresh();
  if (account) document.addEventListener("visibilitychange", onVisible);

  return {
    get lens() {
      return lens;
    },
    setLens(id) {
      lens = id;
    },
    CAP,
    HOLD_DAYS,
    day: D,
    barHeight: () => document.querySelector(".topbar")?.offsetHeight ?? 54,
    goalOn: id => !st.off.includes(id),
    setGoal(id, on) {
      st.off = st.off.filter(x => x !== id);
      if (!on) st.off.push(id);
      save();
      applyGoals();
      emit("goals", { id, on: !!on });
      sync(() => api.setGoal(project.slug, id, !!on));
    },
    held: () => st.held.slice(),
    isHeld: id => st.held.some(h => h.id === id),
    hold(id, opts = {}) {
      const p = paper(id);
      if (!p) return { added: false, evicted: null };
      const existing = st.held.find(h => h.id === id);
      let evicted = null;
      if (existing) existing.at = Date.now();
      else {
        st.held.push({ id, at: Date.now(), from: lens, p: snap(p) });
        if (st.held.length > CAP) {
          // The least recently held or opened goes to the library (DEC-009).
          evicted = st.held.reduce((a, b) => (b.at < a.at ? b : a));
          st.held = st.held.filter(h => h !== evicted);
          addToLibrary(evicted.id);
        }
      }
      save();
      emit("held", { id, added: !existing, evicted: evicted ? evicted.id : null });
      const entry = st.held.find(h => h.id === id);
      sync(() => api.hold(project.slug, id, entry.p, entry.from));
      if (!opts.quiet) {
        toast(
          existing
            ? `Refreshed in working memory: “${short(p.title)}”.`
            : evicted
              ? `Held. Memory was full, so “${short(paper(evicted.id).title)}” moved to your library.`
              : `Held in working memory, ${st.held.length} of ${CAP}.`
        );
      }
      return { added: !existing, evicted: evicted ? evicted.id : null };
    },
    release(id) {
      st.held = st.held.filter(h => h.id !== id);
      save();
      emit("held", { id, released: true });
      sync(() => api.release(project.slug, id));
    },
    rehearse(id) {
      const h = st.held.find(x => x.id === id);
      if (h) h.at = Date.now();
      save();
      emit("held", { id, rehearsed: true });
      if (h) sync(() => api.hold(project.slug, id, h.p, h.from));
    },
    /** Moves everything held to a library, the most recently held on top: "mine", or "team" when signed in. */
    consolidate(shelf = "mine") {
      const n = st.held.length;
      const team = shelf === "team" && account;
      for (const h of st.held.slice().sort((a, b) => a.at - b.at)) {
        if (!team) addToLibrary(h.id);
        else if (!st.team.some(t => t.id === h.id)) st.team.unshift({ id: h.id, at: Date.now(), by: "you", mine: true, canRemove: true, p: h.p });
      }
      st.held = [];
      save();
      emit("held", { consolidated: n });
      sync(() => api.consolidate(project.slug, team ? "team" : null));
      return n;
    },
    library: () => st.library.slice(),
    isKept: id => st.library.some(h => h.id === id),
    /** Keeps a copy in this person's own library, such as one from the team shelf; it stays theirs. */
    keepForMe(id) {
      if (st.library.some(h => h.id === id)) return;
      const from = st.team.find(t => t.id === id) || st.held.find(h => h.id === id);
      const p = from?.p || (paper(id) && snap(paper(id)));
      if (!p) return;
      st.library.unshift({ id, at: Date.now(), p });
      save();
      emit("held", { id, kept: true });
      toast(`In your library: “${short(p.title)}”.`);
      sync(() => api.keep(project.slug, id, p));
    },
    /** Takes a paper out of this person's own library. */
    forget(id) {
      st.library = st.library.filter(h => h.id !== id);
      save();
      emit("held", { id, forgotten: true });
      sync(() => api.forget(project.slug, id));
    },
    // The project's team shelf (DEC-042): signed in only, credited to whoever added each paper.
    canShare: account,
    team: () => st.team.slice(),
    isShared: id => st.team.some(t => t.id === id),
    shareWithTeam(id) {
      if (!account || st.team.some(t => t.id === id)) return;
      const own = st.library.find(h => h.id === id) || st.held.find(h => h.id === id);
      const p = own?.p || (paper(id) && snap(paper(id)));
      if (!p) return;
      st.team.unshift({ id, at: Date.now(), by: "you", mine: true, canRemove: true, p });
      emit("held", { id, shared: true });
      toast(`On the team shelf: “${short(p.title)}”.`);
      sync(() => api.share(project.slug, id, p));
    },
    unshare(id) {
      st.team = st.team.filter(t => t.id !== id);
      emit("held", { id, unshared: true });
      sync(() => api.unshare(project.slug, id));
    },
    fade,
    // In-app routes: /p/<project>/<lens>#<paper or area>. The field lens shows areas, so a paper links to its area.
    link(toLens, id) {
      let target = id || "";
      if (toLens === "field" && paper(id)) target = paper(id).area;
      return `/p/${project.slug}/${toLens}${target ? `#${encodeURIComponent(target)}` : ""}`;
    },
    go: href => navigate(href),
    toast,
    /**
     * An area's items beyond the shortlist, from the day's index (DEC-008): null for a sample day
     * or a day built before the index existed. Loaded once per area and project view.
     */
    areaItems(areaId) {
      if (!D.indexed || D.sample || !D.date) return Promise.resolve(null);
      if (!areaCache.has(areaId)) areaCache.set(areaId, api.dayItems(project.slug, D.date, areaId).catch(() => null));
      return areaCache.get(areaId);
    },
    /** Makes an item from beyond the shortlist known to every lens, so it can be held and opened. */
    adopt(item) {
      if (!D.paperById[item.id]) {
        D.paperById[item.id] = {
          id: item.id,
          area: item.area,
          kind: "paper",
          title: item.title,
          summary: item.summary,
          authors: item.authors,
          url: item.url,
          cats: "",
          minutes: 12,
          signals: [],
          salience: item.salience ?? item.pre,
          arrival: true,
        };
      }
      return D.paperById[item.id];
    },
    // Owners edit the director in a drawer the project view owns; lenses only ask for it (DEC-035).
    canEdit: Boolean(project.canEdit),
    openDirector: () => window.dispatchEvent(new Event("pot:director")),
    /** Opens the explainer for a paper any lens knows (DEC-043); the project view owns its drawer too. */
    explain(id) {
      if (paper(id)) openExplainer(snap(paper(id)));
    },
    /**
     * Opens a paper's score card for this day: the parts its score came from (DEC-008), with how
     * many of the day's shortlist share its score, and what the reader's goal switches did to it:
     *   base     the project's score;  shown  the score on this reader's stage
     *   offGoals the goals this reader switched off
     *   recount  whether switches score the paper again with the best goal still on (DEC-047)
     *   cut      { goal, weight, by } when they can't (a day built before DEC-047) and the paper's best goal is off
     */
    scorecard(id) {
      const p = paper(id);
      if (!p || !D.date || D.sample) return toast(D.sample ? "Sample items have no score card." : "This item has no score card.");
      const base = p.baseSalience ?? p.salience;
      const recount = D.goalSteer != null;
      const offBest = !recount && p.goal && st.off.includes(p.goal) && D.goalById[p.goal];
      window.dispatchEvent(
        new CustomEvent("pot:scorecard", {
          detail: {
            id,
            title: p.title,
            date: D.date,
            base,
            shown: p.salience,
            ties: D.papers.filter(q => q.id !== id && (q.baseSalience ?? q.salience) === base).length,
            offGoals: st.off.slice(),
            recount,
            cut: offBest ? { goal: p.goal, weight: offBest.weight, by: Math.round(OFF_PENALTY * offBest.weight * 1000) / 1000 } : null,
          },
        })
      );
    },
    on(type, fn) {
      if (!listeners.has(type)) listeners.set(type, new Set());
      listeners.get(type).add(fn);
      return () => listeners.get(type)?.delete(fn);
    },
    /** Saved to the account (signed in), or kept in this browser. */
    saved: account,
    /** Stops watching for the page coming back into view, when the project view lets this shell go. */
    close() {
      document.removeEventListener("visibilitychange", onVisible);
    },
  };
}
